type SourceEntry = Record<string, any>;

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.trim().replace(",", "."));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== undefined) return parsed;
  }
  return undefined;
}

function resolvePrice(entry: SourceEntry, isGaebImport: boolean): number {
  if (isGaebImport) {
    return firstNumber(entry.ourPrice, entry.originalPrice, entry.preis, entry.einzelpreis) ?? 0;
  }
  return firstNumber(entry.preis, entry.einzelpreis, entry.ourPrice, entry.originalPrice) ?? 0;
}

function resolveQuantity(entry: SourceEntry): number {
  return firstNumber(entry.adjustedQuantity, entry.originalQuantity, entry.menge) ?? 0;
}

function normalizeMatchKey(value: unknown): string | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const normalized = String(value).trim().toLowerCase();
  return normalized || undefined;
}

function entryHasQuantity(entry: SourceEntry): boolean {
  return resolveQuantity(entry) > 0;
}

export function extractGaebSourceName(database: SourceEntry): string | undefined {
  const description = database.projectInfo?.description;
  if (typeof description !== "string") return undefined;
  const match = description.match(/Erstellt\s+aus\s+X83\s*:\s*(.+)$/i);
  return match?.[1]?.trim() || undefined;
}

export function enrichEntriesWithGaebQuantities(entries: SourceEntry[], gaebEntries: SourceEntry[]) {
  const quantityByPosition = new Map<string, SourceEntry>();

  for (const entry of gaebEntries) {
    if (entry.type && entry.type !== "item") continue;
    if (!entryHasQuantity(entry)) continue;

    for (const key of [
      normalizeMatchKey(entry.positionNumber),
      normalizeMatchKey(entry.artikelId),
      normalizeMatchKey(entry.originalId),
    ]) {
      if (key && !quantityByPosition.has(key)) {
        quantityByPosition.set(key, entry);
      }
    }
  }

  return entries.map((entry) => {
    if (entryHasQuantity(entry)) return entry;

    const match =
      quantityByPosition.get(normalizeMatchKey(entry.positionNumber) || "") ||
      quantityByPosition.get(normalizeMatchKey(entry.artikelId) || "") ||
      quantityByPosition.get(normalizeMatchKey(entry.originalId) || "");

    if (!match) return entry;

    const quantity = resolveQuantity(match);
    return {
      ...entry,
      menge: quantity,
      originalQuantity: firstNumber(match.originalQuantity, match.menge) ?? quantity,
      adjustedQuantity: toFiniteNumber(match.adjustedQuantity),
      positionNumber: entry.positionNumber || match.positionNumber || undefined,
      gewerkNr: entry.gewerkNr || match.gewerkNr || undefined,
      gewerkName: entry.gewerkName || match.gewerkName || undefined,
      untergewerkNr: entry.untergewerkNr || match.untergewerkNr || undefined,
      untergewerkName: entry.untergewerkName || match.untergewerkName || undefined,
      hierarchyLevel: entry.hierarchyLevel ?? match.hierarchyLevel,
      parentId: entry.parentId || match.parentId || undefined,
    };
  });
}

export function createRegularEntriesFromGaeb(entries: SourceEntry[]) {
  return entries
    .filter((entry) => entry.type === "item")
    .map((entry, index) => {
      const quantity = resolveQuantity(entry);
      const price = resolvePrice(entry, true);

      return {
        artikelId: entry.positionNumber || String(index + 1),
        positionNumber: entry.positionNumber || undefined,
        gewerk: entry.gewerk || entry.gewerkName || "",
        gewerkNr: entry.gewerkNr || undefined,
        gewerkName: entry.gewerkName || undefined,
        untergewerkNr: entry.untergewerkNr || undefined,
        untergewerkName: entry.untergewerkName || undefined,
        name: entry.shortText || entry.name || "",
        shortText: entry.shortText || entry.name || "",
        beschreibung: entry.longText || entry.beschreibung || entry.shortText || "",
        longText: entry.longText || entry.beschreibung || undefined,
        einheit: entry.einheit || "Stk",
        preis: price,
        mwst: firstNumber(entry.mwst, entry.umsatzsteuer) ?? 19,
        menge: quantity,
        originalQuantity: firstNumber(entry.originalQuantity, entry.menge),
        adjustedQuantity: toFiniteNumber(entry.adjustedQuantity),
        originalPrice: toFiniteNumber(entry.originalPrice),
        ourPrice: toFiniteNumber(entry.ourPrice),
        type: entry.type,
        hierarchyLevel: toFiniteNumber(entry.hierarchyLevel),
        parentId: entry.parentId || undefined,
        isBedarfsposition: Boolean(entry.isBedarfsposition),
      };
    });
}

export function createQuoteItemsFromDatabaseEntries(
  entries: SourceEntry[],
  options: { isGaebImport: boolean },
) {
  const filteredEntries = options.isGaebImport
    ? entries.filter((entry) => entry.type === "item")
    : entries;

  return filteredEntries.map((entry, index) => {
    const price = resolvePrice(entry, options.isGaebImport);
    const quantity = resolveQuantity(entry);

    return {
      id: index + 1,
      originalId: entry.artikelId || entry.positionNumber || String(index + 1),
      positionNumber: entry.positionNumber || undefined,
      gewerk: entry.gewerk || entry.gewerkName || "",
      gewerkNr: entry.gewerkNr || undefined,
      gewerkName: entry.gewerkName || undefined,
      untergewerkNr: entry.untergewerkNr || undefined,
      untergewerkName: entry.untergewerkName || undefined,
      name: entry.shortText || entry.name || "",
      beschreibung: entry.beschreibung || entry.longText || entry.shortText || "",
      einheit: entry.einheit || "Stk",
      preis: price,
      umsatzsteuer: firstNumber(entry.mwst, entry.umsatzsteuer) ?? 19,
      menge: quantity,
      hierarchyLevel: toFiniteNumber(entry.hierarchyLevel),
      parentPositionNumber: entry.parentPositionNumber || entry.parentId || undefined,
      budgetTaskId: entry.budgetTaskId || entry.taskId || undefined,
    };
  });
}
